import { CurrencyCode } from '@commutifi/constants/Locale'
import { useIntl } from 'locales/index'
import { isNaN, isNil, isFinite } from 'lodash'
import React, { useState } from 'react'
import { AppInputProps, Select as AppSelect, Col, DefaultOptionType, Input, Row } from '@commutifi-fe/ui'
import { useConfig } from 'shared/providers/ConfigProvider'
import styles from './styles.module.scss'
import { Overwrite } from 'utility-types'

export enum FrequencyId {
  PerTrip = 'per_trip',
  PerDay = 'daily',
  PerWeek = 'weekly',
  PerMonth = 'monthly'
}
export const defaultFrequency = FrequencyId.PerMonth

/**
 * Remove all non currency characters from a string (If they are not part of [0-9] '.' ',' or '-')
 * @param formattedCost - Cost value with currency and potentially other invalid characters like a-z, etc.
 * @returns Number with separator characters only
 *
 * @example 1,000,000.00$ output: 1,000,000.00
 * @example 1,000,000a.00$ output: 1,000,000.00
 */
const filterNonCurrencyCharacters = (formattedCost: string | null) => (formattedCost || '').replace(/[^0-9.,-]+/g, '')

/**
 * Find last index of either a number or cost separators (.,)
 * @param formattedCost - Cost value with currency and potentially other invalid characters like a-z, etc.
 * @returns The last index that was either a number or separator used in currencies (.,)
 */
const findLastCostCharacterIndex = (formattedCost: string | null) =>
  (formattedCost || '').split('').findLastIndex((char) => char === '.' || char === ',' || /[\d]/g.test(char || ''))

/**
 * Parse a localized number to a float.
 * @param stringNumber - the localized number
 * @param locale - [optional] the locale that the number is represented in. Omit this parameter to use the current locale.
 */
function parseLocaleNumber(stringNumber: string, locale: string) {
  const thousandSeparator = Intl.NumberFormat(locale)
    .format(11111)
    .replace(/\p{Number}/gu, '')
  const decimalSeparator = Intl.NumberFormat(locale)
    .format(1.1)
    .replace(/\p{Number}/gu, '')

  return parseFloat(
    stringNumber
      .replace(new RegExp(`\\${thousandSeparator}`, 'g'), '')
      .replace(new RegExp(`\\${decimalSeparator}`), '.')
  )
}

const currencyFormatter = (
  amount: string | undefined,
  options: { locale: string; currency: string; intlOptions?: Intl.NumberFormatOptions }
): string => {
  const { currency, locale, intlOptions } = options
  const numberWithoutCurrency = filterNonCurrencyCharacters(amount || '')
  if (
    amount === '' ||
    (typeof amount === 'string' && amount.trim() === '') ||
    isNaN(parseFloat(numberWithoutCurrency))
  ) {
    return ''
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...intlOptions
  }).format(parseLocaleNumber(numberWithoutCurrency, locale))
}

const inputMask = (valueToApplyTemplate: string, value: string, options: { fill?: boolean } = {}) => {
  const { fill } = options
  const templateCurrencyInput = valueToApplyTemplate.replace(/[\d]/g, 'x')

  let inputCostIndex = 0
  const maskedValue = templateCurrencyInput
    .split('')
    .map((char) => {
      if (inputCostIndex >= value.length) {
        // Make sure we keep currencies displayed at the end of the number. If fill option is true, we also
        // make sure that the template 'x's exceeding the value will be filled with 0s
        return char !== 'x' && char !== '.' && char !== ',' ? char : fill && char === 'x' ? '0' : null
      }

      if (char === 'x') {
        // Old value might not have the same format (thousands separator, decimal separator) as the template
        // so we skip the separators characters. We could filter those characters but it's hard to not mess
        // with the string cause the separator and decimal separator might be different based on the locale
        if (value[inputCostIndex] === '.' || value[inputCostIndex] === ',') {
          ++inputCostIndex
        }
        return value[inputCostIndex++]
      }
      if (char === '.' || char === ',') {
        // If the template and our value separator character matches we simply use anyone of them
        if (value[inputCostIndex] === char) {
          return value[inputCostIndex++]
        }
        // If they don't match we skip the value separator character and continue the loop
        if (value[inputCostIndex] === '.' || value[inputCostIndex] === ',') {
          inputCostIndex++
          return
        }
        return char
      }
      return char
    })
    .filter((char) => !isNil(char))
  return maskedValue.join('')
}

const applyCurrencyInputMask = (
  previousValue: string | null,
  newValue: string | null,
  options: { locale: string; currency: string }
) => {
  const { locale, currency } = options
  const filteredPreviousCostValue = filterNonCurrencyCharacters(previousValue)
  const filteredCostTargetValue = filterNonCurrencyCharacters(newValue)

  // Check values difference with only the valid characters to see if we are really making a change
  // or simply ignoring the last invalid user input. Works only if the user is typing new characters
  const diff =
    filteredPreviousCostValue && filteredCostTargetValue
      ? filteredCostTargetValue.replace(filteredPreviousCostValue, '')
      : []

  // Return previous value only if user is typing new characters that are invalid, otherwise we want to process the new value.
  // Also avoid returning on user typing the first character where there is nothing to compare against
  if (diff.length === 0 && previousValue && (newValue?.length || -1) > previousValue.length) {
    return previousValue
  }

  const newCostLastNumberIndex = findLastCostCharacterIndex(filteredCostTargetValue)
  const oldCostLastNumberIndex = findLastCostCharacterIndex(filteredPreviousCostValue)

  // If the user is erasing characters we need to make sure we will skip the actual currency to erase
  // the last number or 'currency like' characters (. or ,) before the currency. When erasing a cost
  // that had the currency at the end the user is actually erasing the currency sign so we can check if
  // the last number index matches
  const costWithoutCurrency =
    diff.length === 0 && newCostLastNumberIndex === oldCostLastNumberIndex
      ? filteredCostTargetValue.substring(0, newCostLastNumberIndex)
      : filteredCostTargetValue
  const intlFormattedValue = currencyFormatter(costWithoutCurrency, { locale, currency })

  return inputMask(intlFormattedValue, costWithoutCurrency)
}

const isCostObject = (cost: any): cost is ICost =>
  Object.prototype.hasOwnProperty.call(cost ?? {}, 'cost') &&
  Object.prototype.hasOwnProperty.call(cost ?? {}, 'paymentFrequency')

export function CostSelector({
  id,
  onChange,
  onBlur,
  value,
  inputProps,
  frequencyOptions,
  disableFrequency,
  currency: controlledCurrency,
  disabled
}: ICostSelectorProps) {
  const intl = useIntl()
  const initialCost = isCostObject(value) ? value?.cost : value
  const initialFrequency = isCostObject(value) ? value?.paymentFrequency || defaultFrequency : defaultFrequency
  const { currency: configCurrency, locale } = useConfig()
  const currency = controlledCurrency || configCurrency

  const [costValue, setCostValue] = useState<string | null>(
    (typeof initialCost === 'number' && isFinite(initialCost)) || typeof initialCost === 'string'
      ? applyCurrencyInputMask(
          null,
          currencyFormatter(initialCost.toString(), { locale, currency, intlOptions: { minimumFractionDigits: 0 } }),
          { locale, currency }
        )
      : null
  )

  const [frequencyValue, setFrequencyValue] = useState(initialFrequency)
  const frequencies = Array.isArray(frequencyOptions)
    ? frequencyOptions
    : [
        {
          id: FrequencyId.PerTrip,
          label: intl.formatMessage({
            id: 'components.costselector.frequencies.per_trip'
          })
        },
        {
          id: FrequencyId.PerDay,
          label: intl.formatMessage({
            id: 'components.costselector.frequencies.per_day'
          })
        },
        {
          id: FrequencyId.PerWeek,
          label: intl.formatMessage({
            id: 'components.costselector.frequencies.per_week'
          })
        },
        {
          id: FrequencyId.PerMonth,
          label: intl.formatMessage({
            id: 'components.costselector.frequencies.per_month'
          })
        }
      ]

  const emitValues = (frequency: string | undefined | null, cost?: string | null) => {
    const parsedCostNumber =
      cost || cost === ''
        ? cost === ''
          ? null
          : parseLocaleNumber(filterNonCurrencyCharacters(cost), locale)
        : costValue && costValue !== ''
          ? parseLocaleNumber(filterNonCurrencyCharacters(costValue), locale)
          : null
    return onChange
      ? disableFrequency
        ? onChange({
            cost: parsedCostNumber,
            paymentFrequency: null
          })
        : onChange({
            cost: parsedCostNumber,
            currency,
            paymentFrequency: frequency || frequencyValue
          })
      : null
  }

  const onFrequencyChange = (value: string) => {
    setFrequencyValue(value)
    emitValues(value)
  }
  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const costWithoutCurrency = filterNonCurrencyCharacters(e.target.value)
    const lastSeparatorIndex = costWithoutCurrency.split('').findLastIndex((char) => char === '.' || char === ',')
    if (
      lastSeparatorIndex >= 0 &&
      lastSeparatorIndex <= costWithoutCurrency.length - 1 &&
      lastSeparatorIndex >= costWithoutCurrency.length - 2
    ) {
      const intlFormattedValue = currencyFormatter(costWithoutCurrency, { locale, currency })

      const maskedValue = inputMask(intlFormattedValue, costWithoutCurrency, { fill: true })
      emitValues(frequencyValue, maskedValue)
      setCostValue(maskedValue)
    }
    onBlur?.(e)
  }

  const onCostChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const maskedValue = applyCurrencyInputMask(costValue, e.target.value, { locale, currency })

    setCostValue(maskedValue)
    emitValues(frequencyValue, maskedValue)

    const newCostLastNumberIndex = findLastCostCharacterIndex(filterNonCurrencyCharacters(e.target.value))
    const oldCostLastNumberIndex = findLastCostCharacterIndex(filterNonCurrencyCharacters(costValue))
    if (
      oldCostLastNumberIndex === newCostLastNumberIndex &&
      (e.currentTarget.selectionStart || -1) >= e.currentTarget.value.length - 1
    ) {
      e.currentTarget.setSelectionRange(newCostLastNumberIndex + 1, newCostLastNumberIndex + 1)
    }
  }
  const handleOnKeyUp: AppInputProps['onKeyUp'] = (e) => {
    const newCostLastNumberIndex = findLastCostCharacterIndex(e.currentTarget.value)
    const oldCostLastNumberIndex = findLastCostCharacterIndex(costValue)

    // Set cursor to last editable character in a cost with currency string
    if (
      oldCostLastNumberIndex === newCostLastNumberIndex &&
      (e.currentTarget.selectionStart || -1) >= e.currentTarget.value.length - 1
    ) {
      e.currentTarget.setSelectionRange(newCostLastNumberIndex + 1, newCostLastNumberIndex + 1)
    }
  }

  return (
    <Row gutter={disableFrequency ? undefined : [16, 16]}>
      <Col span={disableFrequency ? 24 : 12}>
        <Input
          id={id}
          aria-label="Enter Cost"
          data-testid="cost-input"
          placeholder={intl.formatMessage({
            id: 'components.costselector.input.placeholder'
          })}
          value={costValue ?? undefined}
          className={styles['i-cost']}
          onBlur={handleOnBlur}
          onChange={onCostChange}
          onKeyUp={handleOnKeyUp}
          disabled={disabled}
          {...inputProps}
        />
      </Col>
      {disableFrequency ? null : (
        <Col span={12}>
          <AppSelect
            id="cost-frequency"
            value={frequencyValue}
            className={styles['i-frequencies']}
            onChange={onFrequencyChange}
            showSearch={false}
            allowClear={false}
            aria-label="Cost frequency"
            disabled={disabled}
            options={
              frequencyOptions && !Array.isArray(frequencyOptions)
                ? frequencyOptions
                : frequencies.map((frequency) => ({
                    key: frequency.id,
                    value: frequency.id,
                    label: frequency.label
                  }))
            }
          />
        </Col>
      )}
    </Row>
  )
}

export interface ICost {
  cost: number | null
  currency?: string
  paymentFrequency: string | null
}

export interface ICostSelectorProps {
  id?: string
  /**
   * onChange callback
   */
  onChange?: (value: ICost | null) => void
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  /**
   * Controlled value
   */
  value?: Overwrite<ICost, { cost: ICost['cost'] | string }>
  /**
   * Overwrite frequency Select Options. Either without customization and with a simple
   * array with ids and labels.
   */
  frequencyOptions?: DefaultOptionType[]
  /**
   * Overwrite or add some props on the Input component
   */
  inputProps?: React.ComponentProps<typeof Input>
  /**
   * This will hide the selector and leave only the cost input with proper formatter.
   * It will also make sure to return and control a number value instead of an object
   */
  disableFrequency?: boolean
  currency?: CurrencyCode
  disabled?: boolean
}
